/**
 * Multi-Option Variant Picker (Extended from Base)
 * ==================================================
 * Handles products with multiple variant options (e.g., Jersey Type + Limited Number)
 * Manages option selection, finds matching variants, and updates product information.
 *
 * @extends VariantPickerBase
 * @class MultiOptionVariantPicker
 *
 * Features:
 * - Multiple option group selection with dependency clearing
 * - Combined listings support (product URL swapping)
 * - Conditional visibility based on schema configuration
 * - Auto-selection when only one choice available
 * - Product info updates (price, SKU, barcode, weight, media)
 * - Dynamic product title updates
 * - Direct selection for single-option variants
 * - Scroll-to-top handling for combined listing navigation
 */

// Immediately set scroll restoration to manual to prevent browser auto-scroll
if ('scrollRestoration' in history) {
  history.scrollRestoration = 'manual';
}

// Force scroll to top BEFORE any other scripts run (for combined listing navigation)
(function() {
  if (!window.location.hash || window.location.hash === '#top') {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    if (window.location.hash === '#top') {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }
})();

class MultiOptionVariantPicker extends VariantPickerBase {
  /**
   * Creates a new MultiOptionVariantPicker instance
   * @param {HTMLElement} element - The container element for the variant picker
   */
  constructor(element) {
    super(element);

    // Parse variant data from JSON script element
    const variantDataEl = this.container.querySelector('[data-variant-data]');
    const variantDataJson = variantDataEl ? JSON.parse(variantDataEl.textContent) : {};
    this.variants = variantDataJson.variants || [];
    this.formatted = variantDataJson.formatted || {};
    this.config = variantDataJson.config || null;

    // Multi-option state
    this.selectedOptions = {}; // Index -> value mapping
    this.selectedOptionValueIds = {}; // Index -> option value ID mapping (for Section Rendering API)
    this.optionGroups = this.container.querySelectorAll('[data-option-group]');
    this.numberOfOptions = this.optionGroups.length;
    this.optionNames = []; // Store option names for schema lookup
    this.isCombinedListingNavigation = false;

    // High-variant mode detection
    this.highVariantMode = this.container.dataset.highVariantMode === 'true';
    this.productUrl = this.container.dataset.productUrl || '';
    this.sectionId = this.container.dataset.sectionId || '';
    this.totalVariants = parseInt(this.container.dataset.totalVariants) || 0;
    this.selectedVariantIdFromServer = this.container.dataset.selectedVariantId || null;

    // Use high-variant mode if more than 250 variants OR explicitly enabled
    this.useHighVariantMode = this.highVariantMode || this.totalVariants > 250;

    // Extract option names from groups
    this.optionGroups.forEach(group => {
      const optionName = group.dataset.optionName;
      const optionIndex = parseInt(group.dataset.optionIndex);
      this.optionNames[optionIndex] = optionName;
    });

    // Additional element references
    this.summaryEl = this.container.querySelector('[data-selection-summary]');

    // Store original product title for dynamic updates
    this.titleElement = document.querySelector(
      '.product-info__title h1, .product__title h1, [data-product-title]'
    );
    this.originalTitle = this.titleElement ? this.titleElement.textContent.trim() : '';

    this.init();
  }

  /**
   * Initializes the multi-option variant picker
   */
  init() {
    // Validate schema configuration
    if (this.config) {
      this.validateConfig();
    }

    // Setup click delegation for option buttons
    this.container.addEventListener('click', (e) => {
      const button = e.target.closest('[data-option-button]');
      if (button && !button.disabled) {
        const group = button.closest('[data-option-group]');
        const optionIndex = parseInt(group.dataset.optionIndex);
        this.handleOptionSelect(button, optionIndex);
      }
    });

    // Setup search inputs for each option group
    this.optionGroups.forEach(group => {
      const optionIndex = parseInt(group.dataset.optionIndex);
      const searchInput = group.querySelector(`[data-search-input-${optionIndex}]`);
      if (searchInput) {
        searchInput.addEventListener('input', (e) => this.handleOptionSearch(e, optionIndex));
      }
    });

    // Initial state - show disabled button with selection prompt
    this._showAddToCartButton();
    this._disableAddToCartButton(this._getSelectionPromptText());

    // Initialize combined listings pre-selected state
    this.initCombinedListingsState();

    // Ensure numbered grids are sorted numerically (e.g., 1...187)
    this.sortNumberedOptionButtons();

    // Initial state updates
    this.updateSummary();

    this.updateVariantAvailability();
    this.checkURLVariant();

    // Call base init to setup form validation and dispatch ready event
    this._baseInit();
  }

  /**
   * Initialize state for all option groups - detects pre-selected options
   * from Liquid (buttons marked with is-selected class or aria-pressed="true")
   * Works for BOTH combined listings AND regular products
   */
  initCombinedListingsState() {
    this.optionGroups.forEach(group => {
      const isCombinedListing = group.dataset.isCombinedListing === 'true';
      const initialValue = group.dataset.initialValue;
      const optionIndex = parseInt(group.dataset.optionIndex);

      // Only pre-select combined listing options (product type selector)
      // Non-combined-listing options (e.g., numbered editions) start unselected
      if (initialValue && isCombinedListing) {
        this.selectedOptions[optionIndex] = initialValue;

        // Find the selected button
        let selectedButton = group.querySelector('[data-option-button][aria-pressed="true"]');
        if (!selectedButton) {
          selectedButton = group.querySelector('[data-option-button].is-selected');
        }
        if (!selectedButton) {
          selectedButton = group.querySelector(
            `[data-option-button][data-option-value="${initialValue}"]`
          );
        }

        // Capture option value ID for Section Rendering API
        if (selectedButton && selectedButton.dataset.optionValueId) {
          this.selectedOptionValueIds[optionIndex] = selectedButton.dataset.optionValueId;
        }

        // Update the selected value display element
        const selectedValueEl = this.container.querySelector(`[data-selected-value-${optionIndex}]`);
        if (selectedValueEl) {
          const displayValue = selectedButton
            ? (selectedButton.dataset.displayValue || selectedButton.dataset.optionValue)
            : initialValue;

          selectedValueEl.textContent = displayValue;
          selectedValueEl.classList.add('has-selection');
        }

        // Ensure the button is marked as selected
        if (selectedButton) {
          selectedButton.setAttribute('aria-pressed', 'true');
          selectedButton.classList.add('is-selected');
        }
      } else if (initialValue && !isCombinedListing) {
        // Clear any Liquid-set pre-selection on non-combined-listing groups
        const preSelectedButtons = group.querySelectorAll('[data-option-button][aria-pressed="true"], [data-option-button].is-selected');
        preSelectedButtons.forEach(btn => {
          btn.setAttribute('aria-pressed', 'false');
          btn.classList.remove('is-selected');
        });

        const selectedValueEl = this.container.querySelector(`[data-selected-value-${optionIndex}]`);
        if (selectedValueEl) {
          selectedValueEl.textContent = 'Not selected';
          selectedValueEl.classList.remove('has-selection');
        }
      }
    });

    // After setting initial state, try to find matching variant
    if (Object.keys(this.selectedOptions).length > 0) {
      // In high-variant mode with all options selected, use server-provided variant ID
      if (this.useHighVariantMode && this.selectedVariantIdFromServer && this.allOptionsSelected()) {
        this.selectedVariantId = this.selectedVariantIdFromServer;
        this._updateVariantIdInput(this.selectedVariantIdFromServer);

        // Try to find variant in local data for additional info
        const localVariant = this.variants.find(
          v => v.id.toString() === this.selectedVariantIdFromServer
        );
        if (localVariant) {
          this.currentVariant = localVariant;
          // Show/enable add-to-cart button based on variant availability
          if (localVariant.available && !localVariant.blocked_reason) {
            this.updateAddToCartState(true, localVariant);
          } else {
            this.updateAddToCartState(false, localVariant);
          }
        } else {
          // Variant not in local data (truncated), but server confirmed it exists
          // Show the button as enabled since server pre-selected it
          this._showAddToCartButton();
          this._enableAddToCartButton();
        }
      } else {
        this.findAndSelectVariant();
      }
    }
  }

  /**
   * Sort numbered option buttons numerically so lower numbers appear first
   */
  sortNumberedOptionButtons() {
    this.optionGroups.forEach(group => {
      if (group.dataset.isNumberOption !== 'true') return;

      const buttons = Array.from(group.querySelectorAll('[data-option-button]'));
      if (!buttons.length) return;

      const parent = buttons[0].parentElement;
      if (!parent) return;

      const sorted = buttons.slice().sort((a, b) => {
        const aNum = parseInt(a.dataset.displayValue, 10);
        const bNum = parseInt(b.dataset.displayValue, 10);

        if (!isNaN(aNum) && !isNaN(bNum) && aNum !== bNum) {
          return aNum - bNum;
        }

        const aVal = (a.dataset.displayValue || '').toString();
        const bVal = (b.dataset.displayValue || '').toString();
        return aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: 'base' });
      });

      sorted.forEach(btn => parent.appendChild(btn));
    });
  }

  /**
   * Handles option button selection
   * @param {HTMLElement} button - The clicked button
   * @param {number} optionIndex - The index of the option group
   */
  handleOptionSelect(button, optionIndex) {
    if (button.disabled) return;

    const optionValue = button.dataset.optionValue;
    const displayValue = button.dataset.displayValue || optionValue;
    const optionValueId = button.dataset.optionValueId;

    // Check for Combined Listings product swap
    const productUrl = button.dataset.productUrl;
    if (productUrl) {
      const currentProductUrl = this.productSection?.dataset.productUrl;
      if (productUrl !== currentProductUrl) {
        this.handleCombinedListingsSwap(productUrl, button);
        return;
      }
    }

    // Update button states in this group
    const group = button.closest('[data-option-group]');
    const buttons = group.querySelectorAll('[data-option-button]');
    this._updateButtonStates(button, buttons);

    // Clear selections AFTER this option (dependent options), but keep selections BEFORE
    for (let i = optionIndex + 1; i < this.numberOfOptions; i++) {
      delete this.selectedOptions[i];
      delete this.selectedOptionValueIds[i];

      // Reset display text
      const selectedValueEl = this.container.querySelector(`[data-selected-value-${i}]`);
      if (selectedValueEl) {
        selectedValueEl.textContent = 'Not selected';
        selectedValueEl.classList.remove('has-selection');
      }

      // Unselect buttons in subsequent groups
      const otherGroup = this.container.querySelector(
        `[data-option-group][data-option-index="${i}"]`
      );
      if (otherGroup) {
        otherGroup.querySelectorAll('[data-option-button]').forEach(btn => {
          btn.setAttribute('aria-pressed', 'false');
          btn.classList.remove('is-selected');
        });
      }
    }

    // Update state
    this.selectedOptions[optionIndex] = optionValue;
    if (optionValueId) {
      this.selectedOptionValueIds[optionIndex] = optionValueId;
    }

    // Update selected value display
    const selectedValueEl = this.container.querySelector(`[data-selected-value-${optionIndex}]`);
    if (selectedValueEl) {
      selectedValueEl.textContent = displayValue;
      selectedValueEl.classList.add('has-selection');
    }

    // Update product title with selected variant
    this.updateProductTitle();

    // Apply conditional visibility based on selection
    this.applyConditionalVisibility();

    // Update variant availability (uses Section Rendering API in high-variant mode)
    this.updateVariantAvailability();

    // Update summary
    this.updateSummary();

    // Check if this should trigger direct selection
    if (this.shouldDirectSelect(optionIndex, optionValue)) {
      this.handleDirectSelect();
      return;
    }

    // Find and select matching variant
    // In high-variant mode, use Section Rendering API
    if (this.useHighVariantMode && this.allOptionsSelected()) {
      this.fetchVariantByOptionValues();
    } else {
      this.findAndSelectVariant();
    }

    // Auto-select next option if only 1 choice available
    this.autoSelectSingleOption(optionIndex);

    // Scroll to next option on mobile (but not during initialization)
    if (window.innerWidth <= 768 && !this.isInitializing) {
      const nextGroup = group.nextElementSibling;
      if (nextGroup && nextGroup.dataset.optionGroup !== undefined && !nextGroup.hidden) {
        nextGroup.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }

  /**
   * Fetches variant information using Section Rendering API
   * Used for high-variant products (>250 variants) where local JSON is incomplete
   */
  async fetchVariantByOptionValues() {
    // Collect all selected option value IDs
    const optionValueIds = [];
    for (let i = 0; i < this.numberOfOptions; i++) {
      const optionGroup = this.container.querySelector(
        `[data-option-group][data-option-index="${i}"]`
      );
      const isVisible = optionGroup &&
        !optionGroup.hidden &&
        optionGroup.style.display !== 'none';

      if (isVisible && this.selectedOptionValueIds[i]) {
        optionValueIds.push(this.selectedOptionValueIds[i]);
      }
    }

    if (optionValueIds.length === 0) {
      return;
    }

    // Build the Section Rendering API URL
    const url = new URL(this.productUrl, window.location.origin);
    url.searchParams.set('section_id', this.sectionId);
    url.searchParams.set('option_values', optionValueIds.join(','));

    try {
      const response = await fetch(url.toString());
      if (!response.ok) {
        console.error('Section Rendering API error:', response.status);
        // Fallback to local variant lookup
        this.findAndSelectVariant();
        return;
      }

      const html = await response.text();
      this.parseAndApplySectionResponse(html);
    } catch (error) {
      console.error('Failed to fetch variant via Section Rendering API:', error);
      // Fallback to local variant lookup
      this.findAndSelectVariant();
    }
  }

  /**
   * Parses the Section Rendering API response and updates the UI
   * @param {string} html - The HTML response from the Section Rendering API
   */
  parseAndApplySectionResponse(html) {
    // Create a temporary container to parse the response
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Find the variant picker in the response
    const newPicker = tempDiv.querySelector('[data-multi-option-picker]');
    if (!newPicker) {
      console.error('Could not find variant picker in Section Rendering response');
      return;
    }

    // Extract the selected variant ID from the response
    const newSelectedVariantId = newPicker.dataset.selectedVariantId;
    if (newSelectedVariantId) {
      this.selectedVariantId = newSelectedVariantId;

      // Try to find this variant in our local data for additional info
      const localVariant = this.variants.find(v => v.id.toString() === newSelectedVariantId);

      if (localVariant) {
        this.currentVariant = localVariant;
      } else {
        // Extract variant data from the response's JSON script
        const newVariantDataEl = newPicker.querySelector('[data-variant-data]');
        if (newVariantDataEl) {
          try {
            const newVariantData = JSON.parse(newVariantDataEl.textContent);
            const newVariant = newVariantData.variants?.find(
              v => v.id.toString() === newSelectedVariantId
            );
            if (newVariant) {
              this.currentVariant = newVariant;
              // Also update formatted data if available
              if (newVariantData.formatted && newVariantData.formatted[newSelectedVariantId]) {
                this.formatted[newSelectedVariantId] = newVariantData.formatted[newSelectedVariantId];
              }
            }
          } catch (e) {
            console.error('Failed to parse variant data from response:', e);
          }
        }
      }

      // Update hidden input
      this._updateVariantIdInput(newSelectedVariantId);

      // Update URL
      this._updateURL(newSelectedVariantId);
    }

    // Update availability states from the response
    // The server has computed correct availability for all option values
    this.optionGroups.forEach((group, groupIndex) => {
      const newGroup = tempDiv.querySelector(
        `[data-option-group][data-option-index="${groupIndex}"]`
      );
      if (!newGroup) return;

      const buttons = group.querySelectorAll('[data-option-button]');
      buttons.forEach(button => {
        const optionValue = button.dataset.optionValue;
        const newButton = newGroup.querySelector(
          `[data-option-button][data-option-value="${CSS.escape(optionValue)}"]`
        );

        if (newButton) {
          // Update availability from server response
          const isAvailable = newButton.dataset.available === 'true';
          const isSelected = newButton.getAttribute('aria-pressed') === 'true';

          button.disabled = !isAvailable && !isSelected;
          button.classList.toggle('is-unavailable', !isAvailable);
          button.dataset.available = isAvailable;

          // Don't hide buttons based on server response - they exist
          button.classList.remove('is-nonexistent');
          button.style.display = '';
        }
      });
    });

    // Update price display from response
    const newPriceContainer = tempDiv.querySelector('.product-info__price > .price');
    if (newPriceContainer && this.productSection) {
      const currentPriceContainer = this.productSection.querySelector('.product-info__price > .price');
      if (currentPriceContainer) {
        currentPriceContainer.innerHTML = newPriceContainer.innerHTML;
        currentPriceContainer.classList.remove('hidden');
        currentPriceContainer.style.display = '';
      }
    }

    // Update add to cart button state
    const isVariantAvailable = this.currentVariant?.available && !this.currentVariant?.blocked_reason;
    if (this.allOptionsSelected()) {
      if (isVariantAvailable) {
        this._showAddToCartButton();
        this._enableAddToCartButton();
        this._dispatchVariantReadyEvent(this.currentVariant, true);
      } else {
        this._showAddToCartButton();
        this._disableAddToCartButton(
          this.currentVariant?.blocked_reason || 'Sold Out'
        );
        this._dispatchVariantReadyEvent(null, false);
      }

      // Dispatch events
      if (this.currentVariant) {
        this._dispatchThemeVariantChange(this.currentVariant);
        this._dispatchVariantSelectedEvent(this.selectedVariantId, {
          variant: this.currentVariant,
          selectedOptions: this.selectedOptions
        });
      }
    }
  }

  /**
   * Handles Combined Listings product swap
   * @param {string} newProductUrl - URL of the new product to swap to
   * @param {HTMLElement} button - The button that triggered the swap
   */
  handleCombinedListingsSwap(newProductUrl, button) {
    // Store flag in sessionStorage so the new page knows to scroll to top
    try {
      sessionStorage.setItem('mbp_scroll_to_top', 'true');
    } catch (e) {
      // sessionStorage not available
    }

    // Build the new URL
    const url = new URL(window.location.href);
    url.pathname = new URL(newProductUrl, window.location.origin).pathname;
    url.hash = '';
    url.search = '';

    // Navigate to the new product with #top anchor to force scroll to top
    window.location.href = url.toString().split('#')[0] + '#top';
  }

  /**
   * Handles search input for a specific option group
   * @param {Event} event - The input event
   * @param {number} optionIndex - The index of the option group
   */
  handleOptionSearch(event, optionIndex) {
    const group = this.container.querySelector(`[data-option-index="${optionIndex}"]`);
    const buttons = group.querySelectorAll('[data-option-button]');
    const noResultsEl = group.querySelector(`[data-no-results-${optionIndex}]`);

    this._handleSearch(event, buttons, noResultsEl);
  }

  /**
   * Updates variant availability based on current selections
   * Disables buttons that would result in unavailable variants
   *
   * In high-variant mode (>250 variants), uses data-available attributes
   * set by Liquid (which uses product_option_value.available) since the
   * local variants array is incomplete.
   */
  updateVariantAvailability() {
    const currentProductUrl = this.productSection?.dataset.productUrl;

    this.optionGroups.forEach(group => {
      if (group.hidden || group.style.display === 'none') return;

      const optionIndex = parseInt(group.dataset.optionIndex);
      const buttons = group.querySelectorAll('[data-option-button]');

      buttons.forEach(button => {
        // Skip availability check for combined listing buttons (different products)
        const buttonProductUrl = button.dataset.productUrl;
        if (buttonProductUrl && buttonProductUrl !== currentProductUrl) {
          button.disabled = false;
          button.classList.remove('is-unavailable', 'is-nonexistent');
          button.style.display = '';
          return; // Keep button enabled
        }

        // Build test options for checking variant existence
        const optionValue = button.dataset.optionValue;
        const testOptions = { ...this.selectedOptions };
        testOptions[optionIndex] = optionValue;

        // In high-variant mode, detect value TYPE by format rather than existence
        // This prevents hiding values beyond our 250-variant sample
        if (this.useHighVariantMode && optionIndex > 0) {
          const priorSelectedOption = this.selectedOptions[0]; // e.g., "Red Semi-Pro"

          if (!priorSelectedOption) {
            // No first option selected yet, don't override Liquid state
            button.disabled = false;
            button.classList.remove('is-unavailable', 'is-nonexistent');
            button.style.display = '';
            return;
          }

          const isSemiPro = priorSelectedOption.toLowerCase().includes('semi-pro');
          const isReplica = priorSelectedOption.toLowerCase().includes('replica');

          // Detect value format - check multiple patterns for numbered editions
          const isNumberedValue = /^#?\d+/.test(optionValue) || optionValue.includes('/'); // e.g., "#1/187", "1", "#1"
          const isColorValue = /^(red|white|blue|black)$/i.test(optionValue); // e.g., "RED", "WHITE"

          // Hide cross-type values based on format, not existence
          // Semi-Pro jerseys have numbered editions, not color values
          // Replica jerseys have color values, not numbered editions
          if (isSemiPro && isColorValue) {
            button.disabled = true;
            button.classList.add('is-nonexistent');
            button.classList.remove('is-unavailable');
            button.style.display = 'none';
            return;
          }
          if (isReplica && isNumberedValue) {
            button.disabled = true;
            button.classList.add('is-nonexistent');
            button.classList.remove('is-unavailable');
            button.style.display = 'none';
            return;
          }

          // For matching value types in high-variant mode:
          // Trust the data-available attribute set by Liquid (uses product_option_value.available)
          // which works correctly even for products with 2000+ variants
          const isAvailableFromLiquid = button.dataset.available === 'true';

          button.classList.remove('is-nonexistent');
          button.style.display = '';

          if (!isAvailableFromLiquid) {
            // Variant is sold out - keep it disabled with strikethrough
            button.disabled = true;
            button.classList.add('is-unavailable');
            button.setAttribute('data-unavailable', 'true');
          } else {
            // Variant is available
            button.disabled = false;
            button.classList.remove('is-unavailable');
            button.removeAttribute('data-unavailable');
          }
          return;
        }

        // Standard existence check for non-high-variant mode or first option
        const variantExists = this.variants.some(variant => {
          for (let i = 0; i < this.numberOfOptions; i++) {
            const optionGroup = this.container.querySelector(
              `[data-option-group][data-option-index="${i}"]`
            );
            const isGroupVisible = optionGroup &&
              !optionGroup.hidden &&
              optionGroup.style.display !== 'none';

            if (isGroupVisible &&
                i <= optionIndex &&
                testOptions[i] !== undefined &&
                variant.options[i] !== testOptions[i]) {
              return false;
            }
          }
          return true; // Variant exists
        });

        // If variant doesn't exist, hide it
        if (!variantExists) {
          button.disabled = true;
          button.classList.add('is-nonexistent');
          button.classList.remove('is-unavailable');
          button.style.display = 'none';
          return;
        }

        // Standard mode: Check availability using local variants array
        button.classList.remove('is-unavailable', 'is-nonexistent');
        button.style.display = '';

        // Check if variant is available (in stock)
        const hasMatchingVariant = this.variants.some(variant => {
          for (let i = 0; i < this.numberOfOptions; i++) {
            const optionGroup = this.container.querySelector(
              `[data-option-group][data-option-index="${i}"]`
            );
            const isGroupVisible = optionGroup &&
              !optionGroup.hidden &&
              optionGroup.style.display !== 'none';

            if (isGroupVisible &&
                i <= optionIndex &&
                testOptions[i] !== undefined &&
                variant.options[i] !== testOptions[i]) {
              return false;
            }
          }
          return variant.available && !variant.blocked_reason;
        });

        if (!hasMatchingVariant) {
          // Variant exists but sold out - grey out
          button.disabled = true;
          button.classList.add('is-unavailable');
          button.setAttribute('data-unavailable', 'true');
        } else {
          button.disabled = false;
          button.removeAttribute('data-unavailable');
        }
      });
    });
  }

  /**
   * Finds and selects a variant matching the current option selections
   */
  findAndSelectVariant() {
    // Build options array from selected values (only visible groups)
    const optionsArray = [];
    for (let i = 0; i < this.numberOfOptions; i++) {
      const optionGroup = this.container.querySelector(
        `[data-option-group][data-option-index="${i}"]`
      );
      const isVisible = optionGroup &&
        !optionGroup.hidden &&
        optionGroup.style.display !== 'none';

      if (isVisible) {
        optionsArray[i] = this.selectedOptions[i];
      }
    }

    // Find matching variants - only match on visible option indices
    const matchingVariants = this.variants.filter(variant => {
      for (let i = 0; i < this.numberOfOptions; i++) {
        const optionGroup = this.container.querySelector(
          `[data-option-group][data-option-index="${i}"]`
        );
        const isVisible = optionGroup &&
          !optionGroup.hidden &&
          optionGroup.style.display !== 'none';

        if (isVisible && optionsArray[i] && variant.options[i] !== optionsArray[i]) {
          return false;
        }
      }
      return true;
    });

    // Prefer available variants over unavailable ones
    let matchingVariant = matchingVariants.find(v => v.available && !v.blocked_reason);
    if (!matchingVariant && matchingVariants.length > 0) {
      matchingVariant = matchingVariants[0];
    }

    if (matchingVariant) {
      this.currentVariant = matchingVariant;
      this.selectedVariantId = matchingVariant.id;

      // Update hidden input
      this._updateVariantIdInput(matchingVariant.id);

      // Update all product information
      this.updatePrice();
      this.updateSku();
      this.updateBarcode();
      this.updateWeight();
      this.updateMedia();

      // Update add to cart button state
      if (matchingVariant.available && !matchingVariant.blocked_reason) {
        this.updateAddToCartState(true, matchingVariant);
      } else {
        this.updateAddToCartState(false, matchingVariant);
      }

      // Dispatch events
      this._dispatchThemeVariantChange(matchingVariant);
      this._dispatchVariantSelectedEvent(matchingVariant.id, {
        variant: matchingVariant,
        selectedOptions: this.selectedOptions
      });

      // Update URL
      this._updateURL(matchingVariant.id);
    }
  }

  /**
   * Auto-selects the next option if only one choice is available
   * @param {number} currentOptionIndex - The index of the just-selected option
   */
  autoSelectSingleOption(currentOptionIndex) {
    const nextOptionIndex = currentOptionIndex + 1;
    if (nextOptionIndex >= this.numberOfOptions) return;

    const nextGroup = this.container.querySelector(
      `[data-option-group][data-option-index="${nextOptionIndex}"]`
    );
    if (!nextGroup || nextGroup.hidden || nextGroup.style.display === 'none') return;

    // Build current selections up to this point
    const currentSelections = [];
    for (let i = 0; i <= currentOptionIndex; i++) {
      currentSelections[i] = this.selectedOptions[i];
    }

    // Find matching available variants
    const matchingVariants = this.variants.filter(variant => {
      for (let i = 0; i <= currentOptionIndex; i++) {
        if (currentSelections[i] && variant.options[i] !== currentSelections[i]) {
          return false;
        }
      }
      return variant.available && !variant.blocked_reason;
    });

    // If only one variant matches, auto-select its next option
    if (matchingVariants.length === 1) {
      const autoSelectValue = matchingVariants[0].options[nextOptionIndex];
      const buttons = nextGroup.querySelectorAll('[data-option-button]');
      buttons.forEach(btn => {
        if (btn.dataset.optionValue === autoSelectValue) {
          btn.click();
        }
      });
    }
  }

  /**
   * Checks if all visible options are selected
   * @returns {boolean}
   */
  allOptionsSelected() {
    for (let i = 0; i < this.numberOfOptions; i++) {
      const optionGroup = this.container.querySelector(
        `[data-option-group][data-option-index="${i}"]`
      );
      const isVisible = optionGroup &&
        !optionGroup.hidden &&
        optionGroup.style.display !== 'none';

      if (isVisible && !this.selectedOptions[i]) {
        return false;
      }
    }
    return true;
  }

  /**
   * Updates the selection summary UI element
   */
  updateSummary() {
    if (!this.summaryEl) return;

    const contentEl = this.summaryEl.querySelector('[data-summary-content]');
    const actionBtn = this.summaryEl.querySelector('[data-summary-action]');
    const actionText = this.summaryEl.querySelector('[data-summary-action-text]');
    if (!contentEl) return;

    // Remove all state classes
    this.summaryEl.classList.remove('is-complete', 'is-unavailable', 'needs-selection');
    if (actionBtn) actionBtn.style.display = 'none';

    if (!this.allOptionsSelected()) {
      // Find which visible options are not selected
      let visibleCount = 0;
      let selectedVisibleCount = 0;
      let firstUnselectedGroup = null;
      let firstUnselectedName = '';

      for (let i = 0; i < this.numberOfOptions; i++) {
        const optionGroup = this.container.querySelector(
          `[data-option-group][data-option-index="${i}"]`
        );
        const isVisible = optionGroup &&
          !optionGroup.hidden &&
          optionGroup.style.display !== 'none';

        if (isVisible) {
          visibleCount++;
          if (this.selectedOptions[i]) {
            selectedVisibleCount++;
          } else if (!firstUnselectedGroup) {
            firstUnselectedGroup = optionGroup;
            firstUnselectedName = optionGroup.dataset.optionName || 'option';
          }
        }
      }

      const remaining = visibleCount - selectedVisibleCount;

      if (selectedVisibleCount > 0 && firstUnselectedGroup) {
        this.summaryEl.classList.add('needs-selection');
        contentEl.innerHTML = `<strong>Almost there!</strong> Please select your ${firstUnselectedName}`;

        if (actionBtn && actionText) {
          actionText.textContent = `Select ${firstUnselectedName} ↑`;
          actionBtn.style.display = 'flex';

          actionBtn.onclick = () => {
            firstUnselectedGroup.scrollIntoView({ behavior: 'smooth', block: 'center' });
            firstUnselectedGroup.style.transition = 'box-shadow 0.3s ease';
            firstUnselectedGroup.style.boxShadow = '0 0 0 3px #c8102e';
            setTimeout(() => {
              firstUnselectedGroup.style.boxShadow = '';
            }, 1500);
          };
        }
      } else if (selectedVisibleCount === 0) {
        contentEl.textContent = 'Please select your options above';
      } else {
        contentEl.textContent = `${remaining} more option${remaining !== 1 ? 's' : ''} to select`;
      }

      this.summaryEl.style.display = '';
    } else {
      // All visible options selected - hide the summary section
      this.summaryEl.style.display = 'none';
    }
  }

  // ============================================================
  // PRODUCT INFORMATION UPDATE METHODS
  // ============================================================

  /**
   * Updates the displayed price based on current variant
   */
  updatePrice() {
    if (!this.currentVariant || !this.productSection) return;

    const priceContainer = this.productSection.querySelector('.product-info__price > .price');
    if (!priceContainer) return;

    const formattedData = this.formatted[this.currentVariant.id];
    if (!formattedData) return;

    const priceCurrentEl = priceContainer.querySelector('.price__current');
    const priceWasEl = priceContainer.querySelector('.price__was');

    if (priceCurrentEl) {
      priceCurrentEl.innerHTML = formattedData.price;
      priceContainer.classList.remove('hidden');
      priceContainer.style.display = '';
    }

    if (priceWasEl) {
      if (this.currentVariant.compare_at_price &&
          this.currentVariant.compare_at_price > this.currentVariant.price) {
        priceWasEl.innerHTML = formattedData.compareAtPrice || '';
        priceWasEl.classList.remove('hidden');
      } else {
        priceWasEl.innerHTML = '';
        priceWasEl.classList.add('hidden');
      }
    }
  }

  /**
   * Updates the displayed SKU based on current variant
   */
  updateSku() {
    if (!this.currentVariant || !this.productSection) return;

    const skuElement = this.productSection.querySelector('.product-sku__value');
    if (!skuElement) return;

    const skuAvailable = this.currentVariant && this.currentVariant.sku;
    skuElement.textContent = skuAvailable ? this.currentVariant.sku : '';
    skuElement.parentNode.hidden = !skuAvailable;
  }

  /**
   * Updates the displayed barcode based on current variant
   */
  updateBarcode() {
    if (!this.currentVariant || !this.productSection) return;

    const barcodeElement = this.productSection.querySelector('[data-barcode]');
    if (!barcodeElement) return;

    if (this.currentVariant.barcode) {
      barcodeElement.textContent = this.currentVariant.barcode;
      barcodeElement.parentElement?.classList.remove('visibility-hidden');
    } else {
      barcodeElement.parentElement?.classList.add('visibility-hidden');
    }
  }

  /**
   * Updates the displayed weight based on current variant
   */
  updateWeight() {
    if (!this.currentVariant || !this.productSection) return;

    const weightElement = this.productSection.querySelector('[data-weight]');
    if (!weightElement) return;

    const formattedData = this.formatted[this.currentVariant.id];
    if (!formattedData || !formattedData.weight) {
      weightElement.parentElement?.classList.add('visibility-hidden');
      return;
    }

    weightElement.textContent = formattedData.weight;
    weightElement.parentElement?.classList.remove('visibility-hidden');
  }

  /**
   * Updates the media gallery to show variant's featured media
   */
  updateMedia() {
    if (!this.currentVariant || !this.productSection) return;

    const mediaGallery = this.productSection.querySelector('media-gallery');
    if (!mediaGallery || !this.currentVariant.featured_media?.id) return;

    mediaGallery.dispatchEvent(new CustomEvent('variant:media:change', {
      detail: {
        mediaId: this.currentVariant.featured_media.id,
        mediaPosition: this.currentVariant.featured_media.position
      }
    }));
  }

  /**
   * Updates the product title with selected variant info
   */
  updateProductTitle() {
    if (!this.titleElement || !this.originalTitle) return;

    const firstOptionValue = this.selectedOptions[0];

    // Don't append variant name - product title already contains it
    this.titleElement.textContent = this.originalTitle;
  }

  /**
   * Updates add to cart button state
   * @param {boolean} enable - Whether to enable the button
   * @param {Object|null} variant - The current variant
   */
  updateAddToCartState(enable, variant = null) {
    const allSelected = this.allOptionsSelected();

    if (enable && variant && allSelected) {
      this._showAddToCartButton();
      this._enableAddToCartButton();

      // Dispatch variant ready event
      this._dispatchVariantReadyEvent(variant, true);
    } else {
      if (!allSelected) {
        this._showAddToCartButton();
        this._disableAddToCartButton(this._getSelectionPromptText());
      } else {
        this._showAddToCartButton();
        this._disableAddToCartButton(
          variant?.blocked_reason ||
          (variant && !variant.available ? 'Sold Out' : null)
        );
      }

      this._dispatchVariantReadyEvent(null, false);
    }
  }

  // ============================================================
  // CONDITIONAL VISIBILITY METHODS
  // ============================================================

  /**
   * Validates the configuration schema
   * @returns {boolean}
   */
  validateConfig() {
    if (!this.config || typeof this.config !== 'object') {
      this.config = null;
      return false;
    }

    if (!this.config.conditionalOptions) {
      return false;
    }

    return true;
  }

  /**
   * Applies conditional visibility rules based on current selections
   */
  applyConditionalVisibility() {
    // Reset all option groups to visible first
    this.optionGroups.forEach(group => {
      group.hidden = false;
      group.style.display = '';
    });

    if (!this.config || !this.config.conditionalOptions) return;

    const conditionalOptions = this.config.conditionalOptions;

    // Check each selected option for conditional rules
    for (let optionIndex = 0; optionIndex < this.numberOfOptions; optionIndex++) {
      const selectedValue = this.selectedOptions[optionIndex];
      if (!selectedValue) continue;

      const optionName = this.optionNames[optionIndex];
      const optionRules = conditionalOptions[optionName];
      if (!optionRules || !optionRules[selectedValue]) continue;

      const rules = optionRules[selectedValue];

      // Handle hideOptions
      if (rules.hideOptions && Array.isArray(rules.hideOptions)) {
        rules.hideOptions.forEach(optionToHide => {
          const groupToHide = this.findOptionGroupByName(optionToHide);
          if (groupToHide) {
            groupToHide.hidden = true;
            groupToHide.style.display = 'none';
          }
        });
      }

      // Handle showOptions
      if (rules.showOptions && Array.isArray(rules.showOptions)) {
        rules.showOptions.forEach(optionToShow => {
          const groupToShow = this.findOptionGroupByName(optionToShow);
          if (groupToShow) {
            groupToShow.hidden = false;
            groupToShow.style.display = '';
          }
        });
      }
    }
  }

  /**
   * Checks if direct selection should be triggered
   * @param {number} optionIndex - The index of the selected option
   * @param {string} optionValue - The value of the selected option
   * @returns {boolean}
   */
  shouldDirectSelect(optionIndex, optionValue) {
    if (!this.config || !this.config.conditionalOptions) {
      return false;
    }

    const optionName = this.optionNames[optionIndex];
    const conditionalOptions = this.config.conditionalOptions;
    const optionRules = conditionalOptions[optionName];

    if (!optionRules || !optionRules[optionValue]) {
      return false;
    }

    const rules = optionRules[optionValue];

    if (rules.directSelect === true) {
      let visibleOptions = 0;
      let selectedVisibleOptions = 0;

      this.optionGroups.forEach((group, idx) => {
        if (!group.hidden) {
          visibleOptions++;
          if (this.selectedOptions[idx]) {
            selectedVisibleOptions++;
          }
        }
      });

      return visibleOptions === selectedVisibleOptions;
    }

    return false;
  }

  /**
   * Handles direct selection (when all visible options are selected)
   */
  handleDirectSelect() {
    this.findAndSelectVariant();
  }

  /**
   * Finds an option group by its name
   * @param {string} optionName - The name of the option to find
   * @returns {HTMLElement|null}
   */
  findOptionGroupByName(optionName) {
    for (let i = 0; i < this.optionNames.length; i++) {
      if (this.optionNames[i] === optionName) {
        return this.container.querySelector(`[data-option-group][data-option-index="${i}"]`);
      }
    }
    return null;
  }

  /**
   * Checks URL for variant parameter and selects matching variant
   */
  checkURLVariant() {
    const variantId = this._getURLVariantId();
    if (!variantId) return;

    let variant = this.variants.find(v => v.id.toString() === variantId);

    // If URL variant is unavailable, find first available with same first option
    if (variant && (!variant.available || variant.blocked_reason)) {
      const firstOptionValue = variant.options[0];

      const availableWithSameOption = this.variants.find(v =>
        v.options[0] === firstOptionValue &&
        v.available &&
        !v.blocked_reason
      );

      if (availableWithSameOption) {
        variant = availableWithSameOption;
        this._updateURL(variant.id);
      } else {
        const anyAvailable = this.variants.find(v => v.available && !v.blocked_reason);
        if (anyAvailable) {
          variant = anyAvailable;
          this._updateURL(variant.id);
        }
      }
    }

    if (variant) {
      // Only auto-select combined listing options (product type) from URL
      // Non-combined-listing options stay unselected so customer must choose
      variant.options.forEach((optionValue, index) => {
        const group = this.container.querySelector(
          `[data-option-group][data-option-index="${index}"]`
        );
        if (group && group.dataset.isCombinedListing === 'true') {
          const button = group.querySelector(
            `[data-option-button][data-option-value="${optionValue}"]`
          );
          if (button) {
            button.click();
          }
        }
      });

      // Clear the variant from URL since we're not fully selecting it
      this._clearURLVariant();
      this.updateProductTitle();
    }
  }

  // ============================================================
  // PUBLIC API - Abstract Method Implementations
  // ============================================================

  /**
   * Selects a variant by ID
   * @param {string|number} variantId - The variant ID to select
   * @returns {boolean} Whether the selection was successful
   */
  selectVariant(variantId) {
    const variant = this.variants.find(v => v.id.toString() === variantId.toString());
    if (!variant) return false;

    // Select all options for this variant
    variant.options.forEach((optionValue, index) => {
      const group = this.container.querySelector(
        `[data-option-group][data-option-index="${index}"]`
      );
      if (group) {
        const button = group.querySelector(
          `[data-option-button][data-option-value="${optionValue}"]`
        );
        if (button && !button.disabled) {
          button.click();
        }
      }
    });

    return this.selectedVariantId === variantId.toString() ||
           this.selectedVariantId === parseInt(variantId);
  }

  /**
   * Gets the currently selected variant information
   * @returns {Object} Object containing variant information
   */
  getSelectedVariant() {
    return {
      variantId: this.selectedVariantId,
      variant: this.currentVariant,
      selectedOptions: { ...this.selectedOptions },
      optionNames: [...this.optionNames],
      allOptionsSelected: this.allOptionsSelected()
    };
  }

  /**
   * Resets the picker to initial state
   */
  reset() {
    this.selectedOptions = {};
    this.selectedOptionValueIds = {};
    this.selectedVariantId = null;
    this.currentVariant = null;

    // Reset all buttons
    this._resetAllButtonStates('[data-option-button]');
    this.container.querySelectorAll('[data-option-button]').forEach(button => {
      button.disabled = false;
      button.classList.remove('is-unavailable');
    });

    // Reset selected value displays
    this.container.querySelectorAll('[class*="selected-value"]').forEach(el => {
      if (el.dataset.selectedValue !== undefined) {
        el.textContent = 'Not selected';
        el.classList.remove('has-selection');
      }
    });

    // Clear search inputs
    this._clearSearchInputs();

    // Reset hidden input
    this._updateVariantIdInput(null);

    // Reset product title to original
    if (this.titleElement && this.originalTitle) {
      this.titleElement.textContent = this.originalTitle;
    }

    // Reset visibility
    this.applyConditionalVisibility();

    // Update UI
    this.updateSummary();
    this._showAddToCartButton();
    this._disableAddToCartButton(this._getSelectionPromptText());

    // Clear URL variant
    this._clearURLVariant();
  }

  /**
   * Override validation error handler for multi-option context
   * @protected
   */
  _handleValidationError() {
    const firstUnselectedName = this.getFirstUnselectedOptionName();
    alert(`Please select your ${firstUnselectedName || 'options'} first`);
    this._focusFirstInput();
  }

  /**
   * Gets contextual button text for the disabled selection state
   * @returns {string}
   * @private
   */
  _getSelectionPromptText() {
    const name = this.getFirstUnselectedOptionName();
    return name ? `Select Your ${name}` : 'Select Your Options';
  }

  /**
   * Gets the name of the first unselected visible option
   * @returns {string|null}
   */
  getFirstUnselectedOptionName() {
    for (let i = 0; i < this.numberOfOptions; i++) {
      const optionGroup = this.container.querySelector(
        `[data-option-group][data-option-index="${i}"]`
      );
      const isVisible = optionGroup &&
        !optionGroup.hidden &&
        optionGroup.style.display !== 'none';

      if (isVisible && !this.selectedOptions[i]) {
        return optionGroup.dataset.optionName || null;
      }
    }
    return null;
  }
}

// ============================================================
// AUTO-INITIALIZATION
// ============================================================

/**
 * Initializes all multi-option variant pickers on the page
 */
function initMultiOptionVariantPickers() {
  const pickers = document.querySelectorAll('[data-multi-option-picker]');
  const instances = [];

  // Check if this is a combined listing navigation
  let shouldScrollToTop = false;
  try {
    if (sessionStorage.getItem('mbp_scroll_to_top') === 'true') {
      shouldScrollToTop = true;
      sessionStorage.removeItem('mbp_scroll_to_top');
    }
  } catch (e) {
    // sessionStorage not available
  }

  const hashIsTopOrEmpty = !window.location.hash || window.location.hash === '#top';
  if (hashIsTopOrEmpty && window.scrollY > 200) {
    shouldScrollToTop = true;
  }
  if (window.location.hash === '#top') {
    shouldScrollToTop = true;
  }

  pickers.forEach(picker => {
    const instance = new MultiOptionVariantPicker(picker);
    instances.push(instance);
    picker.multiOptionVariantPicker = instance;
  });

  // Robust scroll-to-top for combined listing navigation
  if (instances.length > 0 && hashIsTopOrEmpty) {
    const forceScrollTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    if (window.location.hash === '#top') {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }

    forceScrollTop();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (shouldScrollToTop || window.scrollY > 100) {
          forceScrollTop();
        }
      });
    });

    setTimeout(() => {
      if (shouldScrollToTop || (window.scrollY > 100 && hashIsTopOrEmpty)) {
        forceScrollTop();
      }
    }, 50);

    setTimeout(() => {
      if (shouldScrollToTop || (window.scrollY > 100 && hashIsTopOrEmpty)) {
        forceScrollTop();
      }
    }, 150);

    setTimeout(() => {
      if (shouldScrollToTop || (window.scrollY > 100 && !window.location.hash)) {
        forceScrollTop();
      }
    }, 300);
  }

  window.MultiOptionVariantPickers = instances;
  return instances;
}

// Initialize on DOM ready
VariantPickerBase.initOnReady(initMultiOptionVariantPickers);

// Export for use in other scripts
if (typeof window !== 'undefined') {
  window.MultiOptionVariantPicker = MultiOptionVariantPicker;
}
